import type { AxiosInstance } from 'axios';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';
import { u3202ExtractParameterDefaults } from './utils/u3202ParameterFormUtils';
import { u3202BuildDefaultParameterSchema } from './utils/u3202CatalogModelFormUtils';
import type { U3202ModelCapabilities } from './utils/U3202CapabilitiesTypes';
import { U3202_EMPTY_CAPABILITIES } from './utils/U3202CapabilitiesTypes';

const LOGICALUNIT_URL = 'U3202';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  PROVIDERS_SEARCH: `${LOGICALUNIT_URL}/providers/search`,
  PROVIDER_GET: (id: string) => `${LOGICALUNIT_URL}/providers/${id}`,
  PROVIDERS_CREATE: `${LOGICALUNIT_URL}/providers/create`,
  PROVIDERS_UPDATE: (id: string) => `${LOGICALUNIT_URL}/providers/update/${id}`,
  PROVIDERS_DELETE: (id: string) => `${LOGICALUNIT_URL}/providers/delete/${id}`,
  PROVIDER_AUDITS: (id: string) => `${LOGICALUNIT_URL}/providers/${id}/audits`,
  MODELS_TEST: `${LOGICALUNIT_URL}/models/test`,
  MODELS_DISCOVER: `${LOGICALUNIT_URL}/models/discover`,
  RESOLVE: `${LOGICALUNIT_URL}/resolve`,
};

export type { U3202ModelCapabilities };

export interface U3202InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

export interface U3202ParameterField {
  type: 'number' | 'integer' | 'boolean' | 'string';
  min?: number;
  max?: number;
  default?: number | boolean | string;
}

export type U3202ParameterSchemaDefinition = Record<string, U3202ParameterField>;

/** Ligne recherche fournisseur org (BL-0147). */
export interface U3202OrgProviderConfigSearchRow {
  id: string;
  code: string;
  name: string;
  apiUrl?: string | null;
  showThinking: boolean;
  active: boolean;
  activatedModelCount: number;
  version: number;
}

export interface U3202AiModelInput {
  id: string;
  code: string;
  name: string;
  parameterSchema: U3202ParameterSchemaDefinition;
  defaultParameters?: Record<string, unknown>;
  capabilities: U3202ModelCapabilities;
  active: boolean;
}

export interface U3202AiModelChild extends U3202AiModelInput {
  version: number;
}

export interface U3202ProviderGet {
  id: string;
  code: string;
  name: string;
  apiUrl?: string | null;
  apiKeyMasked: string;
  showThinking: boolean;
  active: boolean;
  version: number;
  models: U3202AiModelChild[];
}

export type U3202ActivatedModelRowState = 'new' | 'modified';

export interface U3202ActivatedModelRow {
  modelId: string;
  modelCode: string;
  modelName: string;
  active: boolean;
  capabilities: U3202ModelCapabilities;
  catalogActive: boolean;
  modelVersion: number;
  parameterSchema: U3202ParameterSchemaDefinition | null;
  defaultParameters: Record<string, unknown>;
  parameterValues: Record<string, unknown>;
  initialParameterDefaults: Record<string, unknown>;
  rowState?: U3202ActivatedModelRowState;
}

export interface U3202OrgModelConfigGridRow extends U3202ActivatedModelRow {
  id: string;
  idChecked?: boolean;
}

export function u3202ActivatedModelToGridRow(row: U3202ActivatedModelRow): U3202OrgModelConfigGridRow {
  return { ...row, id: row.modelId, idChecked: false };
}

export function u3202GridRowToActivatedModel(row: U3202OrgModelConfigGridRow): U3202ActivatedModelRow {
  const { id: _id, idChecked: _checked, ...modelRow } = row;
  return modelRow;
}

export interface U3202OrgProviderConfigFormData {
  id: string;
  providerId: string | null;
  providerCode: string | null;
  providerName: string | null;
  active: boolean;
  apiKey: string;
  apiKeyTouched: boolean;
  hasExistingApiKey: boolean;
  apiUrl: string;
  showThinking: boolean;
  version: number;
  models: U3202ActivatedModelRow[];
  newProviderCode?: string;
  newProviderName?: string;
}

export interface U3202ProviderCreate {
  code: string;
  name: string;
  apiKey: string;
  apiUrl?: string | null;
  showThinking?: boolean;
  active: boolean;
  models?: U3202AiModelInput[];
}

export interface U3202ProviderUpdate {
  id: string;
  code: string;
  name: string;
  apiKey?: string;
  apiUrl?: string | null;
  showThinking?: boolean;
  active: boolean;
  version: number;
  models: U3202AiModelInput[];
}

export interface U3202ProviderDelete {
  id: string;
  version: number;
}

export interface U3202DiscoverModelsRequest {
  providerCode: string;
  apiKey?: string;
  apiUrl?: string | null;
  providerId?: string;
}

export interface U3202DiscoverModelsResponse {
  models: U3202AiModelInput[];
}

export interface U3202ModelTestRequest {
  modelId: string;
  modelCode?: string;
  apiKey?: string;
  apiUrl?: string | null;
  providerId?: string;
  parameterDefaults?: Record<string, unknown>;
}

export interface U3202ModelTestResponse {
  success: boolean;
  providerCode: string;
  modelCode: string;
  modelName: string;
  responsePreview?: string;
  errorMessage?: string;
  usage?: { inputTokens: number; outputTokens: number };
  latencyMs: number;
}

export interface U3202ResolveRequest {
  providerCode?: string;
  modelCode?: string;
  missionId?: string;
  missionCode?: string;
  parameterOverrides?: Record<string, unknown>;
}

export interface U3202ResolveResponse {
  providerCode: string;
  providerName: string;
  modelCode: string;
  modelName: string;
  apiUrl: string;
  parameters: Record<string, unknown>;
  capabilities: U3202ModelCapabilities;
}

export const U3202_ERROR_CODES = {
  API_KEY_MISSING: 'BU320201',
  INVALID_PARAMETERS: 'BU320203',
  MODEL_PROVIDER_MISMATCH: 'BU320207',
  PROVIDER_CODE_DUPLICATE: 'BU320211',
  MODEL_CODE_DUPLICATE: 'BU320212',
  MODEL_NOT_ACTIVATED: 'BU320216',
  MISSION_NOT_FOUND_OR_INACTIVE: 'BU320217',
  OPTIMISTIC_LOCK: 'B00007',
} as const;

export function createEmptyU3202OrgProviderConfigFormData(): U3202OrgProviderConfigFormData {
  return {
    id: '',
    providerId: null,
    providerCode: null,
    providerName: null,
    active: true,
    apiKey: '',
    apiKeyTouched: false,
    hasExistingApiKey: false,
    apiUrl: '',
    showThinking: false,
    version: 0,
    models: [],
    newProviderCode: '',
    newProviderName: '',
  };
}

export function u3202MapModelChildToRow(model: U3202AiModelChild): U3202ActivatedModelRow {
  const defaults = model.defaultParameters ?? {};
  return {
    modelId: model.id,
    modelCode: model.code,
    modelName: model.name,
    active: model.active,
    capabilities: model.capabilities ?? U3202_EMPTY_CAPABILITIES,
    catalogActive: model.active,
    modelVersion: model.version,
    parameterSchema: model.parameterSchema,
    defaultParameters: defaults,
    parameterValues: { ...defaults },
    initialParameterDefaults: { ...defaults },
  };
}

export function normalizeU3202OrgProviderConfigFromApi(provider: U3202ProviderGet): U3202OrgProviderConfigFormData {
  return {
    id: provider.id,
    providerId: provider.id,
    providerCode: provider.code,
    providerName: provider.name,
    active: provider.active,
    apiKey: '',
    apiKeyTouched: false,
    hasExistingApiKey: provider.apiKeyMasked === '***',
    apiUrl: provider.apiUrl ?? '',
    showThinking: provider.showThinking ?? false,
    version: provider.version,
    models: (provider.models ?? []).map(u3202MapModelChildToRow),
  };
}

export function u3202RowToModelInput(row: U3202ActivatedModelRow): U3202AiModelInput {
  return {
    id: row.modelId,
    code: row.modelCode.trim().toLowerCase(),
    name: row.modelName.trim(),
    parameterSchema: row.parameterSchema ?? u3202BuildDefaultParameterSchema(),
    defaultParameters: u3202ExtractParameterDefaults(
      row.parameterSchema,
      row.parameterValues,
    ),
    capabilities: row.capabilities ?? U3202_EMPTY_CAPABILITIES,
    active: row.active,
  };
}

export function u3202FormDataToSearchRow(data: U3202OrgProviderConfigFormData): U3202OrgProviderConfigSearchRow {
  return {
    id: data.id,
    code: data.providerCode ?? data.newProviderCode ?? '',
    name: data.providerName ?? data.newProviderName ?? '',
    apiUrl: data.apiUrl || null,
    showThinking: data.showThinking,
    active: data.active,
    activatedModelCount: data.models.filter((m) => m.active).length,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => axios.get(LogicalUnitEndpoints.INITLU);

export const doSearchProviders = (axios: AxiosInstance, searchPayload: U2000SearchPayload) =>
  axios.post(LogicalUnitEndpoints.PROVIDERS_SEARCH, searchPayload);

export const doGetProvider = (axios: AxiosInstance, id: string) =>
  axios.get(LogicalUnitEndpoints.PROVIDER_GET(id));

export const doCreateProvider = (axios: AxiosInstance, body: U3202ProviderCreate) =>
  axios.post(LogicalUnitEndpoints.PROVIDERS_CREATE, body);

export const doUpdateProvider = (axios: AxiosInstance, id: string, body: U3202ProviderUpdate) =>
  axios.post(LogicalUnitEndpoints.PROVIDERS_UPDATE(id), body);

export const doDeleteProvider = (axios: AxiosInstance, id: string, body: U3202ProviderDelete) =>
  axios.post(LogicalUnitEndpoints.PROVIDERS_DELETE(id), body);

export const doGetProviderAudits = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.PROVIDER_AUDITS(String(id)), { params });
};

export const doTestCatalogModel = (axios: AxiosInstance, body: U3202ModelTestRequest) =>
  axios.post(LogicalUnitEndpoints.MODELS_TEST, body, { skipBusyIndicator: true });

export const doDiscoverModels = (axios: AxiosInstance, body: U3202DiscoverModelsRequest) =>
  axios.post(LogicalUnitEndpoints.MODELS_DISCOVER, body);

export const doResolve = (axios: AxiosInstance, body: U3202ResolveRequest = {}) =>
  axios.post(LogicalUnitEndpoints.RESOLVE, body);

/** @deprecated alias */
export const doSearchOrgProviderConfigs = doSearchProviders;
/** @deprecated alias */
export const doGetOrgProviderConfig = doGetProvider;
/** @deprecated alias */
export const doDeleteOrgProviderConfig = doDeleteProvider;

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string; field?: string };
  switch (apiErr?.errorCode) {
    case U3202_ERROR_CODES.API_KEY_MISSING:
      errorResult.fieldErrors = { ...(errorResult.fieldErrors ?? {}), apiKey: t('errApiKeyRequired', { ns: nameSpace }) };
      break;
    case U3202_ERROR_CODES.INVALID_PARAMETERS: {
      const paramKey = extractParameterKeyFromDescription(apiErr.description);
      if (paramKey) {
        errorResult.fieldErrors = {
          ...(errorResult.fieldErrors ?? {}),
          [`parameterValues.${paramKey}`]: t('errInvalidParameter', { ns: nameSpace, param: paramKey }),
        };
      }
      break;
    }
    case U3202_ERROR_CODES.MODEL_PROVIDER_MISMATCH:
      errorResult.fieldErrors = { ...(errorResult.fieldErrors ?? {}), modelId: t('errModelProviderMismatch', { ns: nameSpace }) };
      break;
    case U3202_ERROR_CODES.PROVIDER_CODE_DUPLICATE:
      errorResult.fieldErrors = { ...(errorResult.fieldErrors ?? {}), newProviderCode: t('errProviderCodeDuplicate', { ns: nameSpace }) };
      break;
    case U3202_ERROR_CODES.MODEL_CODE_DUPLICATE:
      errorResult.fieldErrors = { ...(errorResult.fieldErrors ?? {}), modelCode: t('errModelCodeDuplicate', { ns: nameSpace }) };
      break;
    case U3202_ERROR_CODES.MODEL_NOT_ACTIVATED:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errModelNotActivatedTitle', { ns: nameSpace }), text: t('errModelNotActivated', { ns: nameSpace }) },
      ];
      break;
    case U3202_ERROR_CODES.MISSION_NOT_FOUND_OR_INACTIVE:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        {
          title: t('errMissionNotFoundOrInactiveTitle', { ns: nameSpace }),
          text: t('errMissionNotFoundOrInactive', { ns: nameSpace }),
        },
      ];
      break;
    case U3202_ERROR_CODES.OPTIMISTIC_LOCK:
      errorResult.otherErrors = { ...(errorResult.otherErrors ?? {}), version: t('errOptimisticLock', { ns: nameSpace }) };
      break;
    default:
      break;
  }
}

function extractParameterKeyFromDescription(description?: string): string | null {
  if (!description) return null;
  const match = description.match(/[`'"]([\w]+)[`'"]/);
  return match?.[1] ?? null;
}
